import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import aiService from '../services/aiService';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function Mission() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('input'); // input, generated, editing, complete
  const [formData, setFormData] = useState({
    who_you_help: '',
    what_you_help_them_do: '',
    your_expertise: ''
  });
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [editedMission, setEditedMission] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedMission, setSavedMission] = useState('');
  const [missionHint, setMissionHint] = useState('');

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if business basics is completed
    const statusResult = await businessService.checkBusinessBasicsStatus();
    if (!statusResult.completed) {
      setError('Please complete Business Basics first');
      return;
    }

    // Load existing mission statement if any
    const result = await businessService.getField('mission_statement');
    if (result.success && result.data.field_value) {
      setSavedMission(result.data.field_value);
      setStage('complete');
    }

    // Load previous form inputs and scraped hints
    const fieldsResult = await businessService.getFieldsByNames([
      'who_you_help', 'what_you_help_them_do', 'your_expertise',
      'mission_hint', 'target_audience', 'transformation_promise'
    ]);

    // Get core product from Product table for fallback
    let coreProductName = '';
    try {
      const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        const products = productsData.catalog || [];
        let coreProduct = products.find(p => p.position?.toLowerCase() === 'core');
        if (!coreProduct && products.length > 0) {
          coreProduct = products.find(p => !p.is_primary_tripwire && p.position?.toLowerCase() !== 'tripwire');
        }
        if (coreProduct) {
          coreProductName = coreProduct.name;
        }
      }
    } catch (e) {
      console.error('Error fetching products:', e);
    }

    if (fieldsResult.success && fieldsResult.data.fields) {
      const fields = fieldsResult.data.fields;

      // Pre-fill form with saved or scraped data
      setFormData({
        who_you_help: fields.who_you_help || fields.target_audience || '',
        what_you_help_them_do: fields.what_you_help_them_do || fields.transformation_promise || '',
        your_expertise: fields.your_expertise || coreProductName || ''
      });

      // Load mission hint from scraper
      if (fields.mission_hint) {
        setMissionHint(fields.mission_hint);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const generateOptions = async () => {
    setLoading(true);
    setError('');

    const result = await aiService.generateMission(formData);

    setLoading(false);

    if (result.success) {
      setOptions(result.data.options);
      setStage('generated');
    } else {
      setError(result.error);
    }
  };

  const selectOption = (index) => {
    setSelectedOption(index);
    setEditedMission(options[index]);
  };

  const handleEditChange = (e) => {
    setEditedMission(e.target.value);
  };

  const saveMission = async () => {
    if (!editedMission.trim()) {
      setError('Please select or enter a mission statement');
      return;
    }

    setLoading(true);
    setError('');

    const result = await aiService.saveMission({
      mission_statement: editedMission,
      ...formData
    });

    setLoading(false);

    if (result.success) {
      setSavedMission(editedMission);
      setStage('complete');
    } else {
      setError(result.error);
    }
  };

  const regenerate = async () => {
    setSelectedOption(null);
    setEditedMission('');
    await generateOptions();
  };

  const renderInput = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Mission Statement</h2>

        <p className="text-muted mb-4">
          Your mission statement articulates your company's purpose and value to customers.
          Answer these questions to generate compelling mission statement options.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {missionHint && (
          <Alert variant="info" className="mb-4">
            <strong>From your website:</strong> "{missionHint}"
            <br />
            <small className="text-muted">We've pre-filled the form below based on what we found. Edit as needed.</small>
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3" controlId="who_you_help">
            <Form.Label><strong>Who do you help?</strong></Form.Label>
            <Form.Control
              type="text"
              name="who_you_help"
              value={formData.who_you_help}
              onChange={handleChange}
              placeholder="e.g., busy professionals, small business owners"
              required
            />
            <Form.Text className="text-muted">
              Describe your target audience
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="what_you_help_them_do">
            <Form.Label><strong>What do you help them do?</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="what_you_help_them_do"
              value={formData.what_you_help_them_do}
              onChange={handleChange}
              placeholder="e.g., save time, increase productivity, grow their business"
              required
            />
            <Form.Text className="text-muted">
              What outcome or result do they achieve?
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4" controlId="your_expertise">
            <Form.Label><strong>What expertise do you offer?</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="your_expertise"
              value={formData.your_expertise}
              onChange={handleChange}
              placeholder="e.g., marketing automation, financial planning, web development"
              required
            />
            <Form.Text className="text-muted">
              What unique capabilities or knowledge do you bring?
            </Form.Text>
          </Form.Group>

          <div className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/dashboard/foundation')}
            >
              Save for Later
            </Button>
            <Button
              variant="primary"
              onClick={generateOptions}
              disabled={loading || !formData.who_you_help || !formData.what_you_help_them_do || !formData.your_expertise}
            >
              {loading ? 'Generating...' : 'Generate Mission Statements'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderGenerated = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Choose Your Mission Statement</h2>

        <p className="text-muted mb-4">
          Select the mission statement that best represents your business, or choose one to customize.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {options.map((option, index) => (
          <Card
            key={index}
            className={`mb-3 ${selectedOption === index ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => selectOption(index)}
          >
            <Card.Body>
              <Form.Check
                type="radio"
                name="mission"
                id={`option-${index}`}
                checked={selectedOption === index}
                onChange={() => selectOption(index)}
                label={
                  <div>
                    <h5 className="mb-2">Option {index + 1}</h5>
                    <p className="mb-0">{option}</p>
                  </div>
                }
              />
            </Card.Body>
          </Card>
        ))}

        {selectedOption !== null && (
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Form.Label><strong>Customize (Optional):</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editedMission}
                onChange={handleEditChange}
              />
            </Card.Body>
          </Card>
        )}

        <div className="text-center mb-3">
          <Button
            variant="link"
            onClick={regenerate}
            disabled={loading}
          >
            🔄 Regenerate Options
          </Button>
        </div>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard/foundation')}
          >
            Save for Later
          </Button>
          <Button
            variant="primary"
            onClick={saveMission}
            disabled={loading || selectedOption === null}
          >
            {loading ? 'Saving...' : 'Save Mission Statement'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderComplete = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Mission Statement Complete</h2>

        <Alert variant="success">
          Your mission statement has been saved!
        </Alert>

        <Card className="mb-4 border-success">
          <Card.Body>
            <h5 className="mb-3">Your Mission:</h5>
            <p className="lead">{savedMission}</p>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button variant="primary" onClick={() => setStage('input')}>
            Edit Mission Statement
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {stage === 'input' && renderInput()}
          {stage === 'generated' && renderGenerated()}
          {stage === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default Mission;
